import { Injectable } from '@angular/core';
import { HelpersService } from '@app/shared/services/helpers.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AccountTypesForOTP, ErrorMessages, ShippingProviderNames, ShippingProviders } from '@app/shared/constants';
import {
  AggregationType,
  BulkReprocessResponse,
  CourierPerformanceResponse,
  DashboardResponse,
  FlagShipmentOutlierResponse,
  RegionPerformanceResponse,
  ReprocessShipmentResponse,
  ServiceTypePerformanceResponse,
  ShipmentAggregateCountResponse,
  ShipmentAggregateResponse,
  TimeBucketResponse,
  TimePeriod,
  TriggerMetricsCalculationResponse,
  TrendDirection
} from '../../pages/authenticated/pages/otp/otp.interfaces';

@Injectable({ providedIn: 'root' })
export class OtpService {
  constructor(
    private helperService: HelpersService,
    private http: HttpClient
  ) {}

  public getSupportedCouriers(): { id: ShippingProviders; name: ShippingProviderNames }[] {
    return [
      {
        id: ShippingProviders.CANADA_POST,
        name: ShippingProviderNames.CANADA_POST,
      },
      {
        id: ShippingProviders.CANPAR,
        name: ShippingProviderNames.CANPAR,
      },
      {
        id: ShippingProviders.CHASSEURS_COURRIER,
        name: ShippingProviderNames.CHASSEURS_COURRIER,
      },
      {
        id: ShippingProviders.CHRONO_EXPRESS,
        name: ShippingProviderNames.CHRONO_EXPRESS,
      },
      {
        id: ShippingProviders.DELIVRO,
        name: ShippingProviderNames.DELIVRO,
      },
      {
        id: ShippingProviders.FLASHBOX,
        name: ShippingProviderNames.FLASHBOX,
      },
      {
        id: ShippingProviders.GLS,
        name: ShippingProviderNames.GLS,
      },
      {
        id: ShippingProviders.ICS_COURIER,
        name: ShippingProviderNames.ICS_COURIER,
      },
      {
        id: ShippingProviders.LOOMIS_EXPRESS,
        name: ShippingProviderNames.LOOMIS_EXPRESS,
      },
      {
        id: ShippingProviders.NATIONEX,
        name: ShippingProviderNames.NATIONEX,
      },
      {
        id: ShippingProviders.PUROLATOR,
        name: ShippingProviderNames.PUROLATOR,
      },
      {
        id: ShippingProviders.UPS,
        name: ShippingProviderNames.UPS,
      },
      {
        id: ShippingProviders.ZIPPLY,
        name: ShippingProviderNames.ZIPPLY,
      },
    ];
  }


  public getDashboard(params?: { timeBucket?: string; history?: number; accountType: AccountTypesForOTP }): Observable<DashboardResponse> {
    const headers = this.helperService.getAuthHeaders();

    return this.http
      .get<DashboardResponse>(`${environment.TIMESERIES_SERVICES_URL}/metrics/dashboard`,
        { headers, params }
      )
      .pipe(
        map((res: DashboardResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public getCouriers(params?: {
    timeBucket?: string;
    history?: number;
    couriers?: ShippingProviders[];
    guaranteedOnly?: boolean;
    accountType?: AccountTypesForOTP;
  }): Observable<CourierPerformanceResponse> {
    const headers = this.helperService.getAuthHeaders();
    return this.http
      .get<CourierPerformanceResponse>(`${environment.TIMESERIES_SERVICES_URL}/metrics/couriers`,
        { headers, params }
      )
      .pipe(
        map((res: CourierPerformanceResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public getServiceTypes(params?: {
    timeBucket?: string;
    history?: number;
    couriers?: ShippingProviders[];
    accountType?: AccountTypesForOTP;
  }): Observable<ServiceTypePerformanceResponse> {
    const headers = this.helperService.getAuthHeaders();
    return this.http
      .get<ServiceTypePerformanceResponse>(`${environment.TIMESERIES_SERVICES_URL}/metrics/service-types`,
        { headers, params }
      )
      .pipe(
        map((res: ServiceTypePerformanceResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public getRegions(params?: {
    timeBucket?: string;
    history?: number;
    originCity?: string;
    originProvince?: string;
    destinationCity?: string;
    destinationProvince?: string;
    accountType?: AccountTypesForOTP;
  }): Observable<RegionPerformanceResponse> {
    const headers = this.helperService.getAuthHeaders();
    return this.http
      .get<RegionPerformanceResponse>(`${environment.TIMESERIES_SERVICES_URL}/metrics/regions`,
        { headers, params }
      )
      .pipe(
        map((res: RegionPerformanceResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public getAggregateShipments(
    timeBucket: string,
    aggregateType: AggregationType,
    params: {
      page: number;
      pageSize: number;
      couriers?: ShippingProviders[];
    } = { page: 1, pageSize: 20 }
  ): Observable<ShipmentAggregateResponse> {
    const headers = this.helperService.getAuthHeaders();
    return this.http
      .get<ShipmentAggregateResponse>(
        `${environment.TIMESERIES_SERVICES_URL}/shipments/performance/aggregate/${timeBucket}/${aggregateType}/shipments`,
        { headers, params }
      )
      .pipe(
        map((res: ShipmentAggregateResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public getAggregateShipmentsCount(
    timeBucket: string,
    aggregateType: AggregationType,
    params?: {
      couriers?: ShippingProviders[];
    }
  ): Observable<ShipmentAggregateCountResponse> {
    const headers = this.helperService.getAuthHeaders();
    return this.http
      .get<ShipmentAggregateCountResponse>(
        `${environment.TIMESERIES_SERVICES_URL}/shipments/performance/aggregate/${timeBucket}/${aggregateType}/shipments/count`,
        { headers, params }
      )
      .pipe(
        map((res: ShipmentAggregateCountResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public getTimeBuckets(params?: {
    refresh?: boolean;
    aggregateType?: AggregationType;
  }): Observable<TimeBucketResponse> {
    const headers = this.helperService.getAuthHeaders();

    return this.http
      .get<TimeBucketResponse>(`${environment.TIMESERIES_SERVICES_URL}/metrics/time-buckets`,
        { headers, params }
      )
      .pipe(
        map((res: TimeBucketResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public postRecalculate(params?: {
    date?: string;
    periods?: TimePeriod[];
  }): Observable<TriggerMetricsCalculationResponse> {
    const headers = this.helperService.getAuthHeaders();

    return this.http
      .post<TriggerMetricsCalculationResponse>(
        `${environment.TIMESERIES_SERVICES_URL}/metrics/calculate`,
        params,
        { headers }
      )
      .pipe(
        map((res: TriggerMetricsCalculationResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public flagShipmentAsOutlier(
    shipmentId: number,
    data: { isOutlier: boolean; notes?: string }
  ): Observable<FlagShipmentOutlierResponse> {
    const headers = this.helperService.getAuthHeaders();

    return this.http
      .patch<FlagShipmentOutlierResponse>(
        `${environment.TIMESERIES_SERVICES_URL}/shipments/${shipmentId}/performance/outlier`,
        data,
        { headers }
      )
      .pipe(
        map((res: FlagShipmentOutlierResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public reprocessShipment(
    shipmentId: number,
    data: {
      trackingEventsSource: string;
      companyId: number
    }
  ): Observable<ReprocessShipmentResponse> {
    const headers = this.helperService.getAuthHeaders();

    return this.http
      .post<ReprocessShipmentResponse>(
        `${environment.TIMESERIES_SERVICES_URL}/shipments/${shipmentId}/performance/reprocess`,
        data,
        { headers }
      )
      .pipe(
        map((res: ReprocessShipmentResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public bulkReprocessShipments(
    data: { redisKey: string }
  ): Observable<BulkReprocessResponse> {
    const headers = this.helperService.getAuthHeaders();

    return this.http
      .post<BulkReprocessResponse>(
        `${environment.TIMESERIES_SERVICES_URL}/shipments/performance/bulk-reprocess`,
        data,
        { headers }
      )
      .pipe(
        map((res: BulkReprocessResponse) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public exportAggregateShipments(
    timeBucket: string,
    aggregateType: AggregationType,
    data: {
      couriers?: ShippingProviders[];
      accountType?: AccountTypesForOTP;
      columns?: string[];
      user?: {
        email: string;
        firstName?: string;
        lastName?: string;
      };
    }
  ): Observable<any> {
    const headers = this.helperService.getAuthHeaders();

    return this.http
      .post<any>(
        `${environment.TIMESERIES_SERVICES_URL}/metrics/aggregate/${timeBucket}/${aggregateType}/export`,
        data,
        { headers }
      )
      .pipe(
        map((res: any) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  public getExportJobStatus(jobId: string): Observable<any> {
    const headers = this.helperService.getAuthHeaders();

    return this.http
      .get<any>(
        `${environment.TIMESERIES_SERVICES_URL}/metrics/export/${jobId}/status`,
        { headers }
      )
      .pipe(
        map((res: any) => res),
        catchError((error: any) =>
          throwError(() => error.error || ErrorMessages.UNEXPECTED)
        )
      );
  }

  /**
   * Get the color associated with an account type
   * @param accountType - The account type code ('M' for Machool, 'B' for BYOA)
   * @returns The hex color code for the account type
   */
  public getAccountTypeColor(accountType: string): string {
    switch (accountType) {
      case 'M':
        return '#3f51b5'; // Blue for Machool
      case 'B':
        return '#ff9800'; // Orange for BYOA
      default:
        return '#4caf50'; // Green for others
    }
  }

  /**
   * Get the trend icon based on trend direction
   * @param trend - The trend direction
   * @returns The material icon name
   */
  public getTrendIcon(trend: TrendDirection): string {
    switch (trend) {
      case TrendDirection.IMPROVING:
        return 'trending_up';
      case TrendDirection.DECLINING:
        return 'trending_down';
      case TrendDirection.STABLE:
      default:
        return 'trending_flat';
    }
  }

  /**
   * Get the trend color based on trend direction
   * @param trend - The trend direction
   * @returns The hex color code for the trend
   */
  public getTrendColor(trend: TrendDirection): string {
    switch (trend) {
      case TrendDirection.IMPROVING:
        return '#28a745'; // Green
      case TrendDirection.DECLINING:
        return '#dc3545'; // Red
      case TrendDirection.STABLE:
      default:
        return '#17a2b8'; // Blue
    }
  }

  /**
   * Get the trend text based on trend direction
   * @param trend - The trend direction
   * @returns The human-readable trend text
   */
  public getTrendText(trend: TrendDirection): string {
    switch (trend) {
      case TrendDirection.IMPROVING:
        return 'Improving';
      case TrendDirection.DECLINING:
        return 'Declining';
      case TrendDirection.STABLE:
      default:
        return 'Stable';
    }
  }

  /**
   * Get the claim rate color based on threshold
   * @param rate - The claim rate percentage
   * @returns The hex color code for the claim rate
   */
  public getClaimRateColor(rate: number): string {
    if (rate === 0) return '#6c757d'; // Gray for no claims
    if (rate < 0.5) return '#28a745'; // Green for low claim rate
    if (rate < 1.0) return '#ffc107'; // Yellow for medium claim rate
    return '#dc3545'; // Red for high claim rate
  }
}
